import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FluentProvider } from '@fluentui/react-components';
import { discoveryTheme } from '@/theme/transformrTheme';
import { TenantProvider } from '@/auth/TenantContext';
import { MainLayout } from '@/components/layout/MainLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoginPage } from '@/pages/LoginPage';
import { HomePage } from '@/pages/HomePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { DataPrivacyPage } from '@/pages/DataPrivacyPage';
import { DiscoveryPage } from '@/features/discovery';
import { DeepAnalysisPage } from '@/features/deep-analysis';
import { BookingPage } from '@/pages/BookingPage';
import { useAuth } from '@/hooks/useAuth';

/**
 * Minimal page rendered inside the admin-consent popup.
 * Azure AD redirects here after the admin grants consent.
 * It closes the popup window so control returns to the parent.
 */
function ConsentCallbackPage() {
  React.useEffect(() => {
    window.close();
  }, []);
  return <div style={{ padding: '2rem', textAlign: 'center' }}>Consent granted. This window will close automatically.</div>;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          !isLoading && isAuthenticated
            ? <Navigate to="/" replace />
            : <LoginPage />
        }
      />
      <Route path="/auth/callback" element={<Navigate to="/" replace />} />
      <Route path="/auth/consent-callback" element={<ConsentCallbackPage />} />

      {/* Protected routes with layout */}
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/" element={<HomePage />} />
        <Route path="/readiness" element={<DiscoveryPage />} />
        <Route path="/deep-analysis" element={<DeepAnalysisPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/data-privacy" element={<DataPrivacyPage />} />
        <Route path="/book" element={<BookingPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <FluentProvider theme={discoveryTheme}>
      <QueryClientProvider client={queryClient}>
        {/* TenantProvider resolves access-token roles for the gate (SPA-Auth Standard). */}
        <TenantProvider>
          <Router>
            <AppRoutes />
          </Router>
        </TenantProvider>
      </QueryClientProvider>
    </FluentProvider>
  );
}

export default App;
